// ─── AddOrganisationModal.tsx ─────────────────────────────────────────

import { useCallback, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

// ─── UI Components ─────────────────────────────────────────────
import Modal from "../../common/Modal";

// Styles
import styles from "./AddOrganisation.module.scss";

// ─── Validation Schema ────────────────────────────────────────
import { addOrgSchema, AddOrgFormData } from "./formSchema";

// ─── Form Steps ────────────────────────────────────────────────
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree, { IInviteResult } from "./StepThree";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

interface Props {
  showModal: boolean;
  closeModal: () => void;
}

const AddOrganisationModal = ({ showModal, closeModal }: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [invite, setInvite] = useState<IInviteResult>();

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );

  // Setup React Hook Form with Zod schema
  const methods = useForm<AddOrgFormData>({
    mode: "onChange",
    resolver: zodResolver(addOrgSchema),
    defaultValues: {
      logo: undefined,
      name: "",
      code: "",
      fees: "",
      websiteUrl: "",
      description: "",
    },
  });

  const handleClose = useCallback(() => {
    setStep(1);
    closeModal();
  }, [closeModal]);

  const handleNext = useCallback(
    (value?: IInviteResult) => {
      if (selectedOrganisation) {
        handleClose();
        return;
      }

      setInvite(value);
      setTimeout(
        () => {
          setStep(3);
        },
        value ? 1000 : 10
      );
    },
    [handleClose, selectedOrganisation]
  );

  return (
    <>
      <Modal size="small" visible={showModal} onCancel={handleClose}>
        <div className={styles["container"]}>
          <h1>
            {selectedOrganisation
              ? t("edit_organisation")
              : t("add_organisation")}
          </h1>
          {step !== 3 && !selectedOrganisation && (
            <p className={styles["subtitle"]}>
              {t("add_organisation_subtitle", { val: step })}
            </p>
          )}
          <FormProvider {...methods}>
            {step === 1 && (
              <StepOne
                onNext={() => {
                  if (selectedOrganisation) {
                    handleNext();
                  } else {
                    setStep(2);
                  }
                }}
                onCancel={handleClose}
              />
            )}
            {step === 2 && <StepTwo onNext={handleNext} />}
            {step === 3 && (
              <StepThree
                invite={invite}
                handleClose={handleClose}
                modalOpen={showModal}
              />
            )}
          </FormProvider>
        </div>
      </Modal>
    </>
  );
};

export default AddOrganisationModal;
